<template>
  <div :class="detailOnly ? 'device-detail-host' : 'page'">
    <header v-if="!detailOnly" class="head">
      <div class="title-row">
        <h2 class="h2">전자칠판 관리시스템</h2>
        <div class="title-actions">
          <form class="unified-search" @submit.prevent="applyFilters">
            <input
              v-model="draftQuery"
              class="input"
              type="search"
              aria-label="학교명, 지역, 위치 검색"
              placeholder="학교명, 지역, 위치 검색"
            />
            <button class="btn btn-primary search-button" type="submit">
              검색
            </button>
          </form>
        </div>
      </div>
      <p class="sub">
        {{
          adminScope.restricted
            ? "기기 사용 현황과 상세 기록을 확인할 수 있습니다."
            : "학교별 기기 사용 현황과 상세 기록을 확인할 수 있습니다."
        }}
      </p>
    </header>
    <UsageDashboard
      v-if="!detailOnly"
      :devices="filteredDevices"
      :selected-region="selectedRegion"
      :page="currentPage"
      :page-devices="pagedDevices"
      :total-pages="totalPages"
      :range="displayedRange"
      :usage-summary="usageSummary"
      :status-summary="deviceStatusSummary"
      :format-usage="formatTotalUsage"
      :device-state="getListDeviceState"
      :sort-icon="sortIcon"
      @detail="openDeviceDetail"
      @statistics="statisticsModalOpen = true"
      @pdf="openPdfPreview"
      @region="selectRegion"
      @page="goToPage"
      @sort="toggleSort"
    />

    <!-- 디바이스 수정 모달 -->
    <div
      v-if="detailModalOpen && activeDetailTab === 'edit'"
      class="modal-overlay"
      @click.self="closeEditDeviceModal"
    >
      <div class="modal edit-device-modal">
        <header class="modal-header">
          <div class="modal-heading">
            <div class="modal-icon edit" aria-hidden="true">✎</div>
            <div>
              <div class="modal-title-row">
                <div class="modal-title">
                  {{ editDeviceTarget?.school_name || "-" }}
                </div>
                <span class="modal-location">
                  {{ editDeviceTarget?.app_location || "위치 미지정" }}
                </span>
              </div>
              <p class="modal-subtitle">디바이스 정보 수정</p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="디바이스 수정 모달 닫기"
            :disabled="isUpdatingDevice"
            @click="closeEditDeviceModal"
          >
            ×
          </button>
        </header>
        <nav class="detail-tabs" aria-label="기기 상세 메뉴">
          <button type="button" @click="selectDetailTab('daily')">
            사용시간 조회
          </button>
          <button type="button" @click="selectDetailTab('monthly')">
            월별 사용시간 조회
          </button>
          <button class="active" type="button" aria-current="page">수정</button>
        </nav>

        <form class="edit-device-form" @submit.prevent="updateDevice">
          <label class="edit-field">
            <span>디바이스 UUID</span>
            <input
              v-model="editDeviceForm.deviceUuid"
              class="input"
              type="text"
              readonly
            />
          </label>

          <label class="edit-field school-search-field">
            <span>학교명</span>
            <input
              ref="schoolInputRef"
              v-model.trim="editDeviceForm.schoolName"
              class="input"
              :class="{ invalid: schoolSelectionError }"
              type="search"
              placeholder="학교명을 검색하세요"
              autocomplete="off"
              required
              :disabled="isUpdatingDevice || isLoadingSchools"
              @input="handleSchoolSearchInput"
            />
            <small v-if="selectedEditSchool">
              선택됨: {{ getSchoolLabel(selectedEditSchool) }}
            </small>
            <small v-else-if="isLoadingSchools"
              >학교 목록을 불러오는 중입니다.</small
            >
            <small v-else
              >검색 결과에서 학교를 선택해야 저장할 수 있습니다.</small
            >
            <p v-if="schoolSelectionError" class="school-selection-error">
              {{ schoolSelectionError }}
            </p>
            <div
              v-if="showSchoolSearchResults"
              class="school-search-results"
              role="listbox"
            >
              <button
                v-for="school in editSchoolSearchResults"
                :key="school.school_id"
                class="school-search-option"
                type="button"
                :class="{
                  active:
                    Number(editDeviceForm.schoolId) === Number(school.school_id)
                }"
                :disabled="isUpdatingDevice"
                @click="selectEditSchool(school)"
              >
                <strong>{{ school.name }}</strong>
                <span
                  >{{ school.region || "-" }} · {{ school.office || "-" }} ·
                  {{ school.type || "-" }}</span
                >
              </button>
            </div>
          </label>

          <label class="edit-field">
            <span>디바이스 위치</span>
            <input
              v-model.trim="editDeviceForm.appLocation"
              class="input"
              type="text"
              placeholder="예: 3층 컴퓨터실"
              :disabled="isUpdatingDevice"
            />
          </label>

          <fieldset class="edit-time-field">
            <legend>이전 사용시간</legend>
            <div class="edit-time-inputs">
              <label>
                <input
                  v-model.number="editDeviceForm.baseUsageHours"
                  class="input"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="예: 1002.5"
                  required
                  :disabled="isUpdatingDevice"
                />
                <span>시간</span>
              </label>
            </div>
            <small>
              저장 시 {{ editBaseUsageSeconds.toLocaleString() }}초로
              변환됩니다.
            </small>
          </fieldset>

          <div class="modal-actions edit-modal-actions">
            <button
              class="btn btn-ghost"
              type="button"
              :disabled="isUpdatingDevice"
              @click="closeEditDeviceModal"
            >
              취소
            </button>
            <button
              class="btn btn-primary"
              type="submit"
              :disabled="isUpdatingDevice"
            >
              {{ isUpdatingDevice ? "수정 중..." : "수정" }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 사용시간 조회 모달 -->
    <div
      v-if="detailModalOpen && activeDetailTab === 'daily'"
      class="modal-overlay"
      @click.self="closeDeviceDetail"
    >
      <div class="modal wide">
        <header class="modal-header">
          <div class="modal-heading">
            <div class="modal-icon" aria-hidden="true">⏱</div>
            <div>
              <div class="modal-title-row">
                <div class="modal-title">
                  {{ useTimeTarget?.school_name || "-" }}
                </div>
                <span class="modal-location">
                  {{ useTimeTarget?.app_location || "위치 미지정" }}
                </span>
              </div>
              <p class="modal-subtitle">사용시간 조회 · 일별 사용 현황</p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="모달 닫기"
            @click="closeDeviceDetail"
          >
            ×
          </button>
        </header>
        <nav class="detail-tabs" aria-label="기기 상세 메뉴">
          <button class="active" type="button" aria-current="page">
            사용시간 조회
          </button>
          <button type="button" @click="selectDetailTab('monthly')">
            월별 사용시간 조회
          </button>
          <button type="button" @click="selectDetailTab('edit')">수정</button>
        </nav>

        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-label">총 사용시간</span>
            <strong>{{ secondsToTime(useTimeTarget?.total_usage_sec) }}</strong>
            <span class="summary-note">현재까지 전체 누적 사용시간</span>
          </div>
          <section class="query-panel">
            <div class="query-heading">
              <strong>조회 기간</strong>
              <span>확인할 날짜 범위를 선택하세요.</span>
            </div>
            <div class="date-row">
              <label class="field">
                <div class="label">시작일</div>
                <input
                  v-model="useTimeForm.startDate"
                  class="input"
                  type="date"
                />
              </label>

              <label class="field">
                <div class="label">종료일</div>
                <input
                  v-model="useTimeForm.endDate"
                  class="input"
                  type="date"
                />
              </label>

              <div class="field button-field">
                <button
                  class="btn btn-primary query-button"
                  type="button"
                  @click="getDeviceUseTime"
                >
                  조회하기
                </button>
              </div>
            </div>
          </section>
        </div>

        <section class="chart-section">
          <div class="chart-header">
            <div>
              <strong>일별 사용 추이</strong>
              <span>선택한 기간의 사용시간을 일 단위로 보여줍니다.</span>
            </div>
            <div class="period-total">
              기간 합계 <strong>{{ secondsToTime(useTimePeriodTotal) }}</strong>
            </div>
          </div>

          <div class="daily-usage-legend" aria-label="막대 색상 안내">
            <span
              ><i
                :style="{ backgroundColor: DAILY_USAGE_COLORS.weekdayEnd }"
                aria-hidden="true"
              ></i
              >평일</span
            >
            <span
              ><i
                :style="{ backgroundColor: DAILY_USAGE_COLORS.restDay }"
                aria-hidden="true"
              ></i
              >주말·공휴일</span
            >
          </div>
          <p
            v-if="usageHolidaysFailed"
            class="daily-usage-calendar-note"
            role="status"
          >
            공휴일 정보를 불러오지 못한 달은 주말만 표시합니다.
          </p>
          <div v-if="useTimeLoading" class="chart-empty loading-state">
            <span class="spinner" aria-hidden="true"></span>
            사용시간 데이터를 불러오고 있습니다.
          </div>

          <div v-else-if="useTimeList.length > 0" class="chart-wrap">
            <Bar :data="usageChartData" :options="usageChartOptions" />
          </div>

          <div v-else class="chart-empty">
            조회된 사용시간 데이터가 없습니다.
          </div>
        </section>

        <div class="modal-actions">
          <button
            class="btn btn-ghost"
            type="button"
            @click="closeDeviceDetail"
          >
            닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 월별 사용시간 조회 모달 -->
    <div
      v-if="detailModalOpen && activeDetailTab === 'monthly'"
      class="modal-overlay"
      @click.self="closeDeviceDetail"
    >
      <div class="modal wide">
        <header class="modal-header">
          <div class="modal-heading">
            <div class="modal-icon month" aria-hidden="true">▥</div>
            <div>
              <div class="modal-title-row">
                <div class="modal-title">
                  {{ useMonthTarget?.school_name || "-" }}
                </div>
                <span class="modal-location">
                  {{ useMonthTarget?.app_location || "위치 미지정" }}
                </span>
              </div>
              <p class="modal-subtitle">월별 사용시간 조회</p>
            </div>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="모달 닫기"
            @click="closeDeviceDetail"
          >
            ×
          </button>
        </header>
        <nav class="detail-tabs" aria-label="기기 상세 메뉴">
          <button type="button" @click="selectDetailTab('daily')">
            사용시간 조회
          </button>
          <button class="active" type="button" aria-current="page">
            월별 사용시간 조회
          </button>
          <button type="button" @click="selectDetailTab('edit')">수정</button>
        </nav>

        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-label">총 사용시간</span>
            <strong>{{
              secondsToTime(useMonthTarget?.total_usage_sec)
            }}</strong>
            <span class="summary-note">현재까지 전체 누적 사용시간</span>
          </div>
          <section class="query-panel">
            <div class="query-heading">
              <strong>조회 기간</strong>
              <span>확인할 월 범위를 선택하세요.</span>
            </div>
            <div class="date-row">
              <label class="field">
                <div class="label">시작월</div>
                <input
                  v-model="useMonthForm.startMonth"
                  class="input"
                  type="month"
                />
              </label>

              <label class="field">
                <div class="label">종료월</div>
                <input
                  v-model="useMonthForm.endMonth"
                  class="input"
                  type="month"
                />
              </label>

              <div class="field button-field">
                <button
                  class="btn btn-primary query-button"
                  type="button"
                  @click="getDeviceUseMonth"
                >
                  조회하기
                </button>
              </div>
            </div>
          </section>
        </div>

        <section class="chart-section">
          <div class="chart-header">
            <div>
              <strong>월별 사용 추이</strong>
              <span>선택한 기간의 사용시간을 월 단위로 보여줍니다.</span>
            </div>
            <div class="period-total month">
              기간 합계
              <strong>{{ secondsToTime(useMonthPeriodTotal) }}</strong>
            </div>
          </div>

          <div v-if="useMonthLoading" class="chart-empty loading-state">
            <span class="spinner month" aria-hidden="true"></span>
            월별 사용시간 데이터를 불러오고 있습니다.
          </div>

          <div v-else-if="useMonthList.length > 0" class="chart-wrap">
            <Bar
              :data="usageMonthChartData"
              :options="usageMonthChartOptions"
            />
          </div>

          <div v-else class="chart-empty">
            조회된 월별 사용시간 데이터가 없습니다.
          </div>
        </section>

        <div class="modal-actions">
          <button
            class="btn btn-ghost"
            type="button"
            @click="closeDeviceDetail"
          >
            닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 기기별 사용시간 PDF 미리보기 모달 -->
    <div
      v-if="pdfModalVisible"
      class="modal-overlay pdf-modal-overlay"
      @click.self="closePdfPreview"
    >
      <div class="pdf-modal-card">
        <header class="pdf-modal-header">
          <div>
            <div class="modal-title">기기별 사용시간 PDF 미리보기</div>
            <p class="pdf-modal-subtitle">
              목록과 같은 검색 조건과 월 범위를 적용해 PDF를 생성합니다.
            </p>
          </div>
          <button
            class="modal-close"
            type="button"
            aria-label="PDF 미리보기 닫기"
            @click="closePdfPreview"
          >
            ×
          </button>
        </header>

        <section class="pdf-range-panel">
          <label class="field pdf-filter-field">
            <span class="label">{{
              adminScope.restricted ? "위치 검색" : "목록 필터"
            }}</span>
            <input
              v-model.trim="pdfQuery"
              class="input"
              type="search"
              :placeholder="
                adminScope.restricted
                  ? '위치 검색'
                  : '학교명, 학교지역, 위치 검색'
              "
              :disabled="isGeneratingPdf"
              @input="clearPdfResult"
            />
          </label>
          <label class="field">
            <span class="label">시작월</span>
            <input
              v-model="pdfForm.startMonth"
              class="input"
              type="month"
              :disabled="isGeneratingPdf"
              @change="clearPdfResult"
            />
          </label>
          <span class="range-separator" aria-hidden="true">~</span>
          <label class="field">
            <span class="label">종료월</span>
            <input
              v-model="pdfForm.endMonth"
              class="input"
              type="month"
              :disabled="isGeneratingPdf"
              @change="clearPdfResult"
            />
          </label>
          <button
            class="btn btn-dark"
            type="button"
            :disabled="isGeneratingPdf || !pdfFilteredDevices.length"
            @click="generatePdfPreview"
          >
            {{ isGeneratingPdf ? "PDF 생성 중..." : "미리보기 생성" }}
          </button>
          <span class="pdf-result-summary">
            PDF 대상 {{ pdfFilteredDevices.length }}대
            <template v-if="pdfPreviewUrl">
              · {{ pdfPeriodLabel }} · {{ pdfPageChunks.length }}페이지
            </template>
          </span>
        </section>

        <div class="pdf-modal-body">
          <iframe
            v-if="pdfPreviewUrl"
            :src="pdfPreviewUrl"
            class="pdf-frame"
            title="기기별 사용시간 PDF 미리보기"
          ></iframe>
          <div v-else class="pdf-preview-empty">
            <span v-if="isGeneratingPdf" class="spinner"></span>
            <strong>{{
              isGeneratingPdf
                ? "기기별 사용시간을 불러와 PDF를 만들고 있습니다."
                : "월 범위를 선택한 뒤 미리보기 생성을 눌러주세요."
            }}</strong>
          </div>

          <div class="pdf-capture-area" aria-hidden="true">
            <div ref="pdfCapturePagesRef" class="pdf-capture-stack">
              <article
                v-for="(pageRows, pageIndex) in pdfPageChunks"
                :key="`usage-pdf-${pageIndex}`"
                class="pdf-page"
              >
                <header class="pdf-page-header">
                  <div>
                    <h1>기기별 사용시간</h1>
                    <p>조회 기간: {{ pdfPeriodLabel }}</p>
                  </div>
                  <strong
                    >{{ pageIndex + 1 }} / {{ pdfPageChunks.length }}</strong
                  >
                </header>

                <table class="pdf-table">
                  <thead>
                    <tr>
                      <th class="pdf-col-number">번호</th>
                      <th>학교명</th>
                      <th>학교지역</th>
                      <th>상태</th>
                      <th>위치</th>
                      <th class="pdf-col-usage">총 사용시간</th>
                      <th class="pdf-col-period-usage">
                        {{ pdfPeriodUsageColumnLabel }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in pageRows" :key="row.pdfRowId">
                      <td>{{ row.number }}</td>
                      <td>{{ row?.schoolName || "-" }}</td>
                      <td>{{ row?.schoolRegion || "-" }}</td>
                      <td>{{ row?.statusText || "-" }}</td>
                      <td>{{ row?.location || "-" }}</td>
                      <td>{{ row?.totalUsageText || "-" }}</td>
                      <td class="pdf-usage-cell">
                        {{ row?.periodUsageText || "-" }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <footer class="pdf-page-footer">
                  <span>총 {{ pdfRows.length }}대</span>
                  <span>출력일시 {{ pdfPrintedAt }}</span>
                </footer>
              </article>
            </div>
          </div>
        </div>

        <footer class="pdf-modal-footer">
          <button class="btn btn-ghost" type="button" @click="closePdfPreview">
            닫기
          </button>
          <button
            class="btn btn-primary"
            type="button"
            :disabled="!pdfPreviewUrl || isGeneratingPdf || isDownloadingPdf"
            @click="downloadPdf"
          >
            {{ isDownloadingPdf ? "다운로드 중..." : "PDF 다운로드" }}
          </button>
        </footer>
      </div>
    </div>

    <UsageStatisticsModal
      v-if="statisticsModalOpen"
      :devices="filteredDevices"
      @close="statisticsModalOpen = false"
    />

    <!-- Toast -->
    <div v-if="toast.show" class="toast">{{ toast.message }}</div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
const props = defineProps({
  detailOnly: { type: Boolean, default: false },
  initialDevice: { type: Object, default: null }
});
const emit = defineEmits(["detail-close", "device-updated"]);
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from "vue";
import { deviceApi, requestApi } from "@/api";
import { getUsageHolidays } from "@/api/holidays";
import { DAILY_USAGE_COLORS, isUsageRestDay } from "@/utils/usageCalendar";
import UsageDashboard from "@/components/admin/UsageDashboard.vue";
import { filterDashboardDevices } from "@/utils/usageDashboard";
import UsageStatisticsModal from "@/components/admin/UsageStatisticsModal.vue";
import { statusHours, koreanDate, classifyDevice } from "@/utils/deviceStatus";
import { filterVisibleDevices } from "@/utils/deviceVisibility";

import {
  getAdminScope,
  scopedSchoolName,
  filterSchoolRows
} from "@/utils/adminScope";
const adminScope = getAdminScope();

const statisticsModalOpen = ref(false);

import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineController,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

onMounted(async () => {
  if (props.detailOnly) {
    if (props.initialDevice) openDeviceDetail(props.initialDevice);
    return;
  }
  listCalendarTimer = setInterval(refreshListCalendar, 60000);
  await getDeviceList();
  await loadSchoolList();
});

/**
 * 디바이스 조회
 */
const devices = ref([]);
// API 원본은 보존하고 현재 상태의 지속 시간으로 화면 공통 목록을 만든다.
const visibleDevices = computed(() => filterVisibleDevices(devices.value));
const detailModalOpen = ref(false);
const activeDetailTab = ref("daily");

async function getDeviceList() {
  try {
    const uuid = props.detailOnly ? editDeviceForm.deviceUuid : undefined;
    if (props.detailOnly && !uuid) return;
    const result = await deviceApi.getDevice(uuid, scopedSchoolName());
    console.log("디바이스 조회 결과:", result);

    devices.value = Array.isArray(result) ? result : [];
    if (!props.detailOnly) await refreshListCalendar();
  } catch (error) {
    console.error("디바이스 조회 실패:", error);
    devices.value = [];
    showToast("디바이스 조회 실패");
  }
}

/**
 * 디바이스 정보 수정
 */
const showEditDeviceModal = ref(false);
const editDeviceTarget = ref(null);
const isUpdatingDevice = ref(false);
const editDeviceForm = reactive({
  deviceUuid: "",
  schoolId: "",
  schoolName: "",
  appLocation: "",
  baseUsageHours: 0
});

const schools = ref([]);
const isLoadingSchools = ref(false);
const schoolSearchDirty = ref(false);
const schoolInputRef = ref(null);
const schoolSelectionError = ref("");

const editBaseUsageSeconds = computed(() => {
  const hours = Number(editDeviceForm.baseUsageHours);

  if (!Number.isFinite(hours) || hours < 0) return 0;
  return Math.round(hours * 3600);
});

const selectedEditSchool = computed(() => {
  const schoolId = Number(editDeviceForm.schoolId);

  if (!Number.isInteger(schoolId) || schoolId < 1) return null;
  return (
    schools.value.find((school) => Number(school.school_id) === schoolId) ||
    null
  );
});

const editSchoolSearchResults = computed(() => {
  const keyword = normalizeSchoolKeyword(editDeviceForm.schoolName);

  if (!keyword) return schools.value.slice(0, 8);

  return schools.value
    .filter((school) => {
      return [school.name, school.region, school.office, school.type]
        .map(normalizeSchoolKeyword)
        .some((value) => value.includes(keyword));
    })
    .slice(0, 8);
});

const showSchoolSearchResults = computed(() => {
  if (isLoadingSchools.value || isUpdatingDevice.value) return false;
  return schoolSearchDirty.value && editSchoolSearchResults.value.length > 0;
});

function normalizeSchoolKeyword(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeSchoolList(result) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data?.school_list)) return result.data.school_list;
  if (Array.isArray(result?.school_list)) return result.school_list;
  return [];
}

function getSchoolLabel(school) {
  if (!school) return "-";
  return `${school.name} (${school.region || "-"} / ${school.office || "-"})`;
}

async function loadSchoolList() {
  if (adminScope.restricted) {
    schools.value = devices.value
      .map((device) => ({
        school_id: device.school_id ?? device.school_idx,
        name: device.school_name,
        region: device.school_region
      }))
      .filter((school) => school.school_id);
    return;
  }
  try {
    isLoadingSchools.value = true;
    schools.value = normalizeSchoolList(await requestApi.selectAllSchool());
  } catch (error) {
    console.error("학교 목록 조회 실패:", error);
    schools.value = [];
    showToast("학교 목록 조회 실패");
  } finally {
    isLoadingSchools.value = false;
  }
}

function setEditSchoolFromDevice(device) {
  const schoolId = Number(device.school_id ?? device.school_idx);
  const school =
    schools.value.find((item) => Number(item.school_id) === schoolId) ||
    schools.value.find((item) => item.name === device.school_name);

  editDeviceForm.schoolId =
    school?.school_id ?? (Number.isInteger(schoolId) ? schoolId : "");
  editDeviceForm.schoolName = school?.name || device.school_name || "";
  schoolSearchDirty.value = false;
  schoolSelectionError.value = "";
}

function handleSchoolSearchInput() {
  editDeviceForm.schoolId = "";
  schoolSearchDirty.value = true;
  schoolSelectionError.value = "";
}

function selectEditSchool(school) {
  editDeviceForm.schoolId = school.school_id;
  editDeviceForm.schoolName = school.name;
  schoolSearchDirty.value = false;
  schoolSelectionError.value = "";
}

function openEditDeviceModal(device) {
  if (props.detailOnly && !schools.value.length) loadSchoolList();
  if (!filterSchoolRows([device]).length) return;
  const baseUsageSeconds = Math.max(
    0,
    Math.trunc(Number(device.base_usage_sec) || 0)
  );

  editDeviceTarget.value = device;
  editDeviceForm.deviceUuid = device.device_uuid || "";
  setEditSchoolFromDevice(device);
  editDeviceForm.appLocation = device.app_location || "";
  editDeviceForm.baseUsageHours = Number((baseUsageSeconds / 3600).toFixed(6));
  showEditDeviceModal.value = true;
}

function openDeviceDetail(device) {
  if (!filterSchoolRows([device]).length) return;
  detailModalOpen.value = true;
  activeDetailTab.value = "daily";
  prepareDetailTargets(device);
  openUseTimeModal(device);
}

function prepareDetailTargets(device) {
  editDeviceTarget.value = device;
  useTimeTarget.value = device;
  useMonthTarget.value = device;
}

function selectDetailTab(tab) {
  if (!detailModalOpen.value || activeDetailTab.value === tab) return;
  activeDetailTab.value = tab;
  const device =
    useTimeTarget.value || useMonthTarget.value || editDeviceTarget.value;
  if (!device) return;
  prepareDetailTargets(device);
  if (tab === "daily" && !useTimeList.value.length) openUseTimeModal(device);
  if (tab === "monthly" && !useMonthList.value.length)
    openUseMonthModal(device);
  if (tab === "edit") openEditDeviceModal(device);
}

function closeDeviceDetail() {
  if (isUpdatingDevice.value) return;
  detailModalOpen.value = false;
  activeDetailTab.value = "daily";
  showEditDeviceModal.value = false;
  showUseTimeModal.value = false;
  showUseMonthModal.value = false;
  editDeviceTarget.value = null;
  useTimeTarget.value = null;
  useMonthTarget.value = null;
  useTimeList.value = [];
  useMonthList.value = [];
  emit("detail-close");
}

function closeEditDeviceModal() {
  if (isUpdatingDevice.value) return;

  closeDeviceDetail();
  schoolSearchDirty.value = false;
  schoolSelectionError.value = "";
}

async function updateDevice() {
  if (isUpdatingDevice.value) return;

  const schoolId = Number(editDeviceForm.schoolId);
  const hours = Number(editDeviceForm.baseUsageHours);

  if (!editDeviceForm.deviceUuid) {
    showToast("디바이스 UUID를 확인해주세요.");
    return;
  }

  if (
    !Number.isInteger(schoolId) ||
    schoolId < 1 ||
    !selectedEditSchool.value
  ) {
    schoolSelectionError.value =
      "학교명을 직접 입력하지 말고, 검색 결과에서 학교를 선택해주세요.";
    schoolSearchDirty.value = true;
    schoolInputRef.value?.focus();
    showToast("검색 결과에서 학교를 선택해주세요.");
    return;
  }

  if (!Number.isFinite(hours) || hours < 0) {
    showToast("이전 사용시간을 0 이상의 시간 값으로 입력해주세요.");
    return;
  }

  try {
    isUpdatingDevice.value = true;
    const updated = await deviceApi.updateDevice({
      device_uuid: editDeviceForm.deviceUuid,
      school_id: schoolId,
      app_location: editDeviceForm.appLocation,
      base_usage_sec: editBaseUsageSeconds.value
    });

    if (!updated) {
      showToast("디바이스 정보를 수정하지 못했습니다.");
      return;
    }

    await getDeviceList();
    const refreshed = devices.value.find(
      (device) => device.device_uuid === editDeviceForm.deviceUuid
    );
    if (refreshed) {
      prepareDetailTargets(refreshed);
      openEditDeviceModal(refreshed);
    }
    isUpdatingDevice.value = false;
    showToast("디바이스 정보가 수정되었습니다.");
    emit("device-updated");
  } catch (error) {
    console.error("디바이스 정보 수정 실패:", error);
    showToast("디바이스 정보 수정 중 오류가 발생했습니다.");
  } finally {
    isUpdatingDevice.value = false;
  }
}

/**
 * 목록 필터
 */
const draftQuery = ref("");
const appliedFilters = reactive({ query: "" });
const selectedRegion = ref("");
function selectRegion(region) {
  selectedRegion.value = region;
  currentPage.value = 1;
}
const sortState = reactive({ field: "status", direction: "asc" });
const pageSize = 10;
const currentPage = ref(1);

function isDeviceActive(device) {
  return String(device?.status || "").toUpperCase() === "ACTIVE";
}

function getDeviceStatusLabel(device) {
  return isDeviceActive(device) ? "활성" : "비활성";
}

function normalizeSearch(value) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("ko");
}

function getStatusTimeValue(device) {
  return statusHours(device);
}

const listCalendar = reactive({
  date: koreanDate(),
  holidays: new Set(),
  ready: false,
  failed: false
});
let listCalendarTimer;
let calendarRequest = 0;
onBeforeUnmount(() => {
  clearInterval(listCalendarTimer);
  calendarRequest++;
});
async function refreshListCalendar() {
  const request = ++calendarRequest;
  const date = koreanDate();
  if (listCalendar.date !== date) listCalendar.ready = false;
  listCalendar.date = date;
  try {
    const result = await getUsageHolidays(date, date);
    if (request !== calendarRequest) return;
    listCalendar.holidays = result.dates;
    listCalendar.failed = result.failed;
    listCalendar.ready = !result.failed;
  } catch {
    if (request !== calendarRequest) return;
    listCalendar.ready = false;
    listCalendar.failed = true;
  }
}
function getListDeviceState(device) {
  return classifyDevice(device, listCalendar);
}
function compareKorean(a, b) {
  return String(a || "").localeCompare(String(b || ""), "ko", {
    numeric: true,
    sensitivity: "base"
  });
}

function compareStatusPolicy(a, b, reverse = false) {
  const activeA = isDeviceActive(a);
  const activeB = isDeviceActive(b);
  if (activeA !== activeB) {
    return reverse ? (activeA ? 1 : -1) : activeA ? -1 : 1;
  }
  const hoursA = getStatusTimeValue(a);
  const hoursB = getStatusTimeValue(b);
  if (hoursA === null || hoursB === null) {
    if (hoursA !== hoursB) return hoursA === null ? 1 : -1;
    return compareKorean(a.school_name, b.school_name);
  }
  const timeDifference = hoursA - hoursB;
  if (!timeDifference) return compareKorean(a.school_name, b.school_name);
  if (reverse) return activeA ? timeDifference : -timeDifference;
  return activeA ? -timeDifference : timeDifference;
}

function toggleSort(field) {
  if (sortState.field === field) {
    sortState.direction = sortState.direction === "asc" ? "desc" : "asc";
  } else {
    sortState.field = field;
    sortState.direction = "asc";
  }
  currentPage.value = 1;
}

function sortIcon(field) {
  if (sortState.field !== field) return "↕";
  return sortState.direction === "asc" ? "▲" : "▼";
}

function searchDevices(source, currentFilters) {
  const scoped = filterSchoolRows(Array.isArray(source) ? source : []);
  return filterDashboardDevices(
    scoped,
    currentFilters.query,
    selectedRegion.value
  );
}

function sortDevices(list, sort) {
  return [...list].sort((a, b) => {
    if (sort.field === "status") {
      return compareStatusPolicy(a, b, sort.direction === "desc");
    }
    if (sort.field === "totalUsage") {
      const result = getUsageSeconds(a) - getUsageSeconds(b);
      return sort.direction === "asc" ? result : -result;
    }
    const fields = {
      schoolName: "school_name",
      schoolRegion: "school_region"
    };
    const result = compareKorean(a[fields[sort.field]], b[fields[sort.field]]);
    return sort.direction === "asc" ? result : -result;
  });
}

const filteredDevices = computed(() =>
  sortDevices(searchDevices(visibleDevices.value, appliedFilters), sortState)
);

const listDevices = computed(() => filteredDevices.value);

const totalPages = computed(() =>
  Math.max(1, Math.ceil(listDevices.value.length / pageSize))
);

const pagedDevices = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return listDevices.value.slice(start, start + pageSize);
});

const displayedRange = computed(() => {
  if (!listDevices.value.length) return "0대 표시";
  const start = (currentPage.value - 1) * pageSize + 1;
  const end = Math.min(currentPage.value * pageSize, listDevices.value.length);
  return `${start} - ${end} 표시`;
});

watch(totalPages, (pageCount) => {
  if (currentPage.value > pageCount) currentPage.value = pageCount;
});

function goToPage(page) {
  currentPage.value = Math.min(Math.max(1, page), totalPages.value);
}

function applyFilters() {
  appliedFilters.query = draftQuery.value.trim();
  currentPage.value = 1;
}

const deviceStatusSummary = computed(() => ({
  active: filteredDevices.value.filter(isDeviceActive).length,
  inactive: filteredDevices.value.filter((device) => !isDeviceActive(device))
    .length
}));

const usageSummary = computed(() => {
  const values = filteredDevices.value.map(getUsageSeconds);
  const total = values.reduce((sum, value) => sum + value, 0);
  const count = values.length;
  const average = count ? Math.round(total / count) : 0;
  return { count, total, average, averageText: formatTotalUsage(average) };
});
function getUsageSeconds(device) {
  return Math.max(0, Number(device?.total_usage_sec) || 0);
}

/**
 * 기기별 사용시간 PDF
 */
const pdfModalVisible = ref(false);
const pdfCapturePagesRef = ref(null);
const pdfRows = ref([]);
const pdfPageChunks = ref([]);
const pdfPreviewUrl = ref("");
const currentPdfBlob = ref(null);
const isGeneratingPdf = ref(false);
const isDownloadingPdf = ref(false);
const pdfPrintedAt = ref("");
const pdfQuery = ref("");

const pdfForm = reactive({
  startMonth: "",
  endMonth: ""
});

const pdfFilteredDevices = computed(() =>
  filteredDevices.value.filter((device) => {
    const keyword = normalizeSearch(pdfQuery.value);
    // 학교 관리자는 담당 학교 범위 안에서 위치만 검색한다.
    // 원본 목록은 filterAndSortDevices에서 공통 학교 제한을 다시 적용한다.
    const searchValues = adminScope.restricted
      ? [device.app_location]
      : [device.school_name, device.school_region, device.app_location];
    return (
      !keyword ||
      searchValues.some((value) => normalizeSearch(value).includes(keyword))
    );
  })
);

const pdfPeriodLabel = computed(() => {
  if (!pdfForm.startMonth || !pdfForm.endMonth) return "-";

  const formatLabel = (value) => {
    const [year, month] = value.split("-");
    return `${year}년 ${Number(month)}월`;
  };

  if (pdfForm.startMonth === pdfForm.endMonth) {
    return formatLabel(pdfForm.startMonth);
  }

  return `${formatLabel(pdfForm.startMonth)} ~ ${formatLabel(
    pdfForm.endMonth
  )}`;
});

const pdfPeriodUsageColumnLabel = computed(() => {
  if (!pdfForm.startMonth || !pdfForm.endMonth) return "선택 기간 사용시간";

  const [startYear, startMonth] = pdfForm.startMonth.split("-");
  const [endYear, endMonth] = pdfForm.endMonth.split("-");

  if (pdfForm.startMonth === pdfForm.endMonth) {
    return `${Number(startMonth)}월 사용시간`;
  }

  if (startYear === endYear) {
    return `${Number(startMonth)}월~${Number(endMonth)}월 사용시간`;
  }

  return `${startYear}년 ${Number(startMonth)}월~${endYear}년 ${Number(
    endMonth
  )}월 사용시간`;
});

function cleanupPdfPreviewUrl() {
  if (pdfPreviewUrl.value) {
    URL.revokeObjectURL(pdfPreviewUrl.value);
    pdfPreviewUrl.value = "";
  }
}

function clearPdfResult() {
  cleanupPdfPreviewUrl();
  currentPdfBlob.value = null;
  pdfRows.value = [];
  pdfPageChunks.value = [];
}

function openPdfPreview() {
  if (!filteredDevices.value.length) {
    showToast("PDF로 만들 기기가 없습니다.");
    return;
  }

  const currentMonth = formatMonth(new Date());
  pdfQuery.value = "";
  pdfForm.startMonth = currentMonth;
  pdfForm.endMonth = currentMonth;
  clearPdfResult();
  pdfModalVisible.value = true;
}

function closePdfPreview() {
  pdfModalVisible.value = false;
  clearPdfResult();
}

function getMonthlyList(result, uuid) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.[uuid])) return result[uuid];
  return [];
}

async function buildPdfRows(sourceDevices) {
  const rows = new Array(sourceDevices.length);
  let cursor = 0;
  const workerCount = Math.min(6, sourceDevices.length);

  const worker = async () => {
    while (cursor < sourceDevices.length) {
      const index = cursor;
      cursor += 1;

      const device = sourceDevices[index];
      const result = await deviceApi.getDeviceUseMonth(
        device.device_uuid,
        pdfForm.startMonth,
        pdfForm.endMonth
      );
      const monthlyList = getMonthlyList(result, device.device_uuid);
      const totalSeconds = monthlyList.reduce(
        (sum, item) =>
          sum + Number(item.total_time_sec ?? item.usage_time_sec ?? 0),
        0
      );

      rows[index] = {
        pdfRowId: `${device.device_uuid || "device"}-${index}`,
        number: index + 1,
        schoolName: device.school_name || "-",
        schoolRegion: device.school_region || "-",
        statusText: getDeviceStatusLabel(device),
        location: device.app_location || "-",
        totalUsageText: formatTotalUsage(device.total_usage_sec),
        periodUsageText: formatTotalUsage(totalSeconds)
      };
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return rows;
}

function chunkPdfRows(rows) {
  const rowsPerPage = 15;
  const pages = [];

  for (let index = 0; index < rows.length; index += rowsPerPage) {
    pages.push(rows.slice(index, index + rowsPerPage));
  }

  return pages;
}

async function renderPdfBlob() {
  const container = pdfCapturePagesRef.value;

  if (!container) throw new Error("PDF 캡처 영역이 없습니다.");

  const pageElements = Array.from(container.querySelectorAll(".pdf-page"));
  if (!pageElements.length) throw new Error("PDF 페이지가 없습니다.");

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4"
  });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 5;
  const availableWidth = pageWidth - margin * 2;
  const availableHeight = pageHeight - margin * 2;

  for (let index = 0; index < pageElements.length; index += 1) {
    const canvas = await html2canvas(pageElements[index], {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false
    });
    const imageData = canvas.toDataURL("image/jpeg", 0.94);
    const ratio = Math.min(
      availableWidth / canvas.width,
      availableHeight / canvas.height
    );
    const renderWidth = canvas.width * ratio;
    const renderHeight = canvas.height * ratio;

    if (index > 0) pdf.addPage();
    pdf.addImage(
      imageData,
      "JPEG",
      (pageWidth - renderWidth) / 2,
      (pageHeight - renderHeight) / 2,
      renderWidth,
      renderHeight
    );
  }

  return pdf.output("blob");
}

async function generatePdfPreview() {
  if (isGeneratingPdf.value) return;

  if (!pdfForm.startMonth || !pdfForm.endMonth) {
    showToast("시작월과 종료월을 선택해주세요.");
    return;
  }

  if (pdfForm.startMonth > pdfForm.endMonth) {
    showToast("시작월은 종료월보다 늦을 수 없습니다.");
    return;
  }

  const sourceDevices = pdfFilteredDevices.value.map((device) => ({
    ...device
  }));
  if (!sourceDevices.length) {
    showToast("PDF로 만들 기기가 없습니다.");
    return;
  }

  try {
    isGeneratingPdf.value = true;
    clearPdfResult();
    pdfRows.value = await buildPdfRows(sourceDevices);
    pdfPageChunks.value = chunkPdfRows(pdfRows.value);
    pdfPrintedAt.value = new Intl.DateTimeFormat("ko-KR", {
      dateStyle: "medium",
      timeStyle: "short"
    }).format(new Date());

    await nextTick();
    const blob = await renderPdfBlob();
    currentPdfBlob.value = blob;
    pdfPreviewUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    console.error("기기별 사용시간 PDF 생성 실패:", error);
    clearPdfResult();
    showToast("PDF 미리보기를 생성하지 못했습니다.");
  } finally {
    isGeneratingPdf.value = false;
  }
}

function getPdfFileName() {
  return `기기별_사용시간_${pdfForm.startMonth}_${pdfForm.endMonth}.pdf`;
}

async function downloadPdf() {
  if (!currentPdfBlob.value || isDownloadingPdf.value) return;

  try {
    isDownloadingPdf.value = true;
    const url = URL.createObjectURL(currentPdfBlob.value);
    const link = document.createElement("a");
    link.href = url;
    link.download = getPdfFileName();
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("기기별 사용시간 PDF 다운로드 실패:", error);
    showToast("PDF 다운로드 중 오류가 발생했습니다.");
  } finally {
    isDownloadingPdf.value = false;
  }
}

onBeforeUnmount(() => {
  cleanupPdfPreviewUrl();
});

/**
 * 사용시간 조회
 */
const showUseTimeModal = ref(false);
const useTimeTarget = ref(null);
const useTimeList = ref([]);
const useTimeLoading = ref(false);

const useTimePeriodTotal = computed(() =>
  useTimeList.value.reduce(
    (total, item) => total + Number(item.usage_time_sec || 0),
    0
  )
);

const useTimeForm = reactive({
  startDate: "",
  endDate: ""
});

// Inputs are drafts; the chart range changes only with a successful query.
const appliedUseTimeRange = reactive({ startDate: "", endDate: "" });
let useTimeRequest = 0;

function formatDate(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

function getDefaultDateRange() {
  const today = new Date();
  const before30Days = new Date();

  before30Days.setDate(today.getDate() - 30);

  return {
    startDate: formatDate(before30Days),
    endDate: formatDate(today)
  };
}

function openUseTimeModal(device) {
  if (!filterSchoolRows([device]).length) return;
  detailModalOpen.value = true;
  activeDetailTab.value = "daily";
  prepareDetailTargets(device);
  useTimeTarget.value = device;
  useTimeList.value = [];

  const range = getDefaultDateRange();
  useTimeForm.startDate = range.startDate;
  useTimeForm.endDate = range.endDate;

  showUseTimeModal.value = true;

  getDeviceUseTime();
}

const usageHolidays = ref(new Set());
const usageHolidaysFailed = ref(false);
let usageHolidayRequest = 0;

async function refreshUsageHolidays(startDate, endDate) {
  const request = ++usageHolidayRequest;
  usageHolidays.value = new Set();
  usageHolidaysFailed.value = false;
  const result = await getUsageHolidays(startDate, endDate);
  if (request !== usageHolidayRequest) return;
  usageHolidays.value = result.dates;
  usageHolidaysFailed.value = result.failed;
}

async function getDeviceUseTime() {
  if (!useTimeTarget.value) return;

  const uuid = useTimeTarget.value.device_uuid;
  const { startDate, endDate } = useTimeForm;
  const request = ++useTimeRequest;
  void refreshUsageHolidays(startDate, endDate);

  try {
    useTimeLoading.value = true;

    const result = await deviceApi.getDeviceUse(uuid, startDate, endDate);
    if (request !== useTimeRequest) return;
    console.log("사용시간 조회 결과:", result);

    Object.assign(appliedUseTimeRange, { startDate, endDate });
    useTimeList.value = Array.isArray(result) ? result : [];
  } catch (error) {
    if (request !== useTimeRequest) return;
    console.error("사용시간 조회 실패:", error);
    useTimeList.value = [];
    showToast("사용시간 조회 실패");
  } finally {
    if (request === useTimeRequest) useTimeLoading.value = false;
  }
}

/**
 * 월별 사용시간 조회
 */
const showUseMonthModal = ref(false);
const useMonthTarget = ref(null);
const useMonthList = ref([]);
const useMonthLoading = ref(false);

const useMonthPeriodTotal = computed(() =>
  useMonthList.value.reduce(
    (total, item) => total + Number(item.total_time_sec || 0),
    0
  )
);

const useMonthForm = reactive({
  startMonth: "",
  endMonth: ""
});

function formatMonth(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");

  return `${yyyy}-${mm}`;
}

function getDefaultMonthRange() {
  const today = new Date();
  const before12Months = new Date();

  before12Months.setMonth(today.getMonth() - 11);

  return {
    startMonth: formatMonth(before12Months),
    endMonth: formatMonth(today)
  };
}

function openUseMonthModal(device) {
  if (!filterSchoolRows([device]).length) return;
  detailModalOpen.value = true;
  activeDetailTab.value = "monthly";
  prepareDetailTargets(device);
  useMonthTarget.value = device;
  useMonthList.value = [];

  const range = getDefaultMonthRange();
  useMonthForm.startMonth = range.startMonth;
  useMonthForm.endMonth = range.endMonth;

  showUseMonthModal.value = true;

  getDeviceUseMonth();
}

async function getDeviceUseMonth() {
  if (!useMonthTarget.value) return;

  const uuid = useMonthTarget.value.device_uuid;
  const { startMonth, endMonth } = useMonthForm;

  if (!startMonth || !endMonth) {
    showToast("시작월과 종료월을 선택해주세요.");
    return;
  }

  if (startMonth > endMonth) {
    showToast("시작월은 종료월보다 늦을 수 없습니다.");
    return;
  }

  try {
    useMonthLoading.value = true;

    const result = await deviceApi.getDeviceUseMonth(
      uuid,
      startMonth,
      endMonth
    );

    console.log("월별 사용시간 조회 결과:", result);

    const monthList = result?.[uuid];

    useMonthList.value = Array.isArray(monthList) ? monthList : [];
  } catch (error) {
    console.error("월별 사용시간 조회 실패:", error);
    useMonthList.value = [];
    showToast("월별 사용시간 조회 실패");
  } finally {
    useMonthLoading.value = false;
  }
}

/**
 * 사용시간 차트
 */
function secondsToTime(sec) {
  const totalSec = Number(sec || 0);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);

  if (h > 0) return `${h}시간 ${m}분`;
  return `${m}분`;
}

function formatTotalUsage(sec) {
  const totalSec = Math.max(0, Number(sec) || 0);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);

  return `${hours}시간 ${minutes}분`;
}

function getDateRangeList(startDate, endDate) {
  const list = [];

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  const current = new Date(start);

  while (current <= end) {
    list.push(formatDate(current));
    current.setDate(current.getDate() + 1);
  }

  return list;
}

function groupUsageByDate(list, startDate, endDate) {
  const map = {};

  list.forEach((item) => {
    const date = item.usage_date;
    const seconds = Number(item.usage_time_sec || 0);

    if (!date) return;

    map[date] = (map[date] || 0) + seconds;
  });

  return getDateRangeList(startDate, endDate).map((date) => {
    const seconds = map[date] || 0;

    return {
      date,
      seconds,
      hours: Number((seconds / 3600).toFixed(2))
    };
  });
}

function createBarGradient(context, startColor, endColor) {
  const { chart } = context;
  const { ctx, chartArea } = chart;

  if (!chartArea) return startColor;

  const gradient = ctx.createLinearGradient(
    0,
    chartArea.bottom,
    0,
    chartArea.top
  );
  gradient.addColorStop(0, startColor);
  gradient.addColorStop(1, endColor);

  return gradient;
}

function createUsageChartOptions(axisLabel) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 650,
      easing: "easeOutQuart"
    },
    interaction: {
      mode: "index",
      intersect: false
    },
    layout: {
      padding: {
        top: 8
      }
    },
    parsing: {
      xAxisKey: "x",
      yAxisKey: "y"
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        displayColors: false,
        backgroundColor: "#0f172a",
        titleColor: "#cbd5e1",
        bodyColor: "#ffffff",
        padding: 12,
        cornerRadius: 10,
        titleFont: {
          size: 12,
          weight: "normal"
        },
        bodyFont: {
          size: 13,
          weight: "bold"
        },
        callbacks: {
          label(context) {
            const seconds = context.raw?.seconds || 0;
            return `사용시간  ${secondsToTime(seconds)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        border: {
          display: false
        },
        grid: {
          color: "rgba(148, 163, 184, 0.18)",
          drawTicks: false
        },
        ticks: {
          color: "#94a3b8",
          padding: 10,
          font: {
            size: 11
          },
          callback(value) {
            return `${value}시간`;
          }
        }
      },
      x: {
        border: {
          display: false
        },
        grid: {
          display: false
        },
        title: {
          display: true,
          text: axisLabel,
          color: "#94a3b8",
          padding: {
            top: 12
          },
          font: {
            size: 11,
            weight: "normal"
          }
        },
        ticks: {
          color: "#64748b",
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 12,
          font: {
            size: 11
          }
        }
      }
    }
  };
}

const usageChartData = computed(() => {
  const grouped = groupUsageByDate(
    useTimeList.value,
    appliedUseTimeRange.startDate,
    appliedUseTimeRange.endDate
  );

  return {
    labels: grouped.map((item) => item.date),
    datasets: [
      {
        label: "사용시간",
        data: grouped.map((item) => ({
          x: item.date,
          y: item.hours,
          seconds: item.seconds
        })),
        backgroundColor: (context) =>
          isUsageRestDay(grouped[context.dataIndex]?.date, usageHolidays.value)
            ? DAILY_USAGE_COLORS.restDay
            : createBarGradient(
                context,
                DAILY_USAGE_COLORS.weekdayStart,
                DAILY_USAGE_COLORS.weekdayEnd
              ),
        hoverBackgroundColor: (context) =>
          isUsageRestDay(grouped[context.dataIndex]?.date, usageHolidays.value)
            ? DAILY_USAGE_COLORS.restDay
            : DAILY_USAGE_COLORS.weekdayHover,
        borderWidth: 0,
        borderRadius: 7,
        borderSkipped: false,
        maxBarThickness: 34,
        categoryPercentage: 0.72,
        barPercentage: 0.82
      }
    ]
  };
});

const usageChartOptions = createUsageChartOptions("날짜");

const usageMonthChartData = computed(() => {
  return {
    labels: useMonthList.value.map((item) => item.usage_month),
    datasets: [
      {
        label: "월별 사용시간",
        data: useMonthList.value.map((item) => ({
          x: item.usage_month,
          y: Number((Number(item.total_time_sec || 0) / 3600).toFixed(2)),
          seconds: Number(item.total_time_sec || 0)
        })),
        backgroundColor: (context) =>
          createBarGradient(context, "#a78bfa", "#7c3aed"),
        hoverBackgroundColor: "#6d28d9",
        borderWidth: 0,
        borderRadius: 7,
        borderSkipped: false,
        maxBarThickness: 42,
        categoryPercentage: 0.68,
        barPercentage: 0.82
      }
    ]
  };
});

const usageMonthChartOptions = createUsageChartOptions("월");

/**
 * Toast
 */
const toast = reactive({
  show: false,
  message: ""
});

let toastTimer = null;

function showToast(msg) {
  toast.message = msg;
  toast.show = true;

  if (toastTimer) clearTimeout(toastTimer);

  toastTimer = setTimeout(() => {
    toast.show = false;
    toast.message = "";
  }, 2200);
}
</script>

<style scoped>
.device-detail-host {
  display: contents;
}
.usage-stats.school-admin-stats {
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
}
@media (max-width: 760px) {
  .usage-stats.school-admin-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}
.page {
  min-height: 100%;
  box-sizing: border-box;
  padding: 24px 20px 40px;
  background: #f4f7fb;
  color: #1f2937;
  display: grid;
  align-content: start;
  gap: 20px;
}

.head {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
}

.eyebrow {
  margin-bottom: 6px;
  color: #2563eb;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.h2 {
  margin: 0;
  color: #111827;
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.03em;
}

.sub {
  margin: 7px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: #6b7280 !important;
}

.device-count {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 126px;
  padding: 12px 16px;
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.88);
  color: #64748b;
  font-size: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);
}

.device-count strong {
  margin-left: auto;
  color: #1d4ed8;
  font-size: 24px;
  line-height: 1;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

.strong {
  color: #111827;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.35;
}

.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-end;
  padding-bottom: 16px;
  border-bottom: 1px solid #edf1f6;
}

.tool-left {
  display: flex;
  flex: 1;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(145px, 1fr));
}

.filter-field {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.filter-field > span {
  color: #475569;
  font-size: 12px;
  font-weight: 800;
}

.filter-field .input {
  width: 100%;
  min-width: 0;
}

.status-counts {
  display: flex;
  gap: 8px;
  align-items: center;
}

.status-counts .active-count {
  color: #2563eb;
}

.tool-right {
  display: flex;
  gap: 10px;
  align-items: center;
}

.search-button {
  height: 42px;
  min-width: 88px;
}

.input {
  box-sizing: border-box;
  min-width: 240px;
  height: 42px;
  border: 1px solid #dbe2ea;
  border-radius: 11px;
  padding: 0 13px;
  outline: none;
  background: #f8fafc;
  color: #1f2937;
  font-size: 13px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    background 0.18s ease;
}

.input:focus {
  border-color: #3b82f6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.input::placeholder {
  color: #94a3b8;
}

.tool-left .input {
  width: min(100%, 420px);
}

.usage-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.stat-card {
  display: grid;
  gap: 5px;
  min-height: 104px;
  box-sizing: border-box;
  padding: 17px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.statistics-open-button {
  border: 1px solid #262626;
  border-radius: 0;
  background: #fff;
  color: #262626;
  padding: 12px 10px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.statistics-open-button:hover {
  background: #f0f0f0;
}
.statistics-open-button:focus-visible {
  outline: 2px solid #41748e;
  outline-offset: 2px;
}

.stat-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
}

.stat-card strong {
  overflow: hidden;
  color: #0f172a;
  font-size: 21px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-note {
  color: #94a3b8;
  font-size: 12px;
}

.outlier-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.outlier-list-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
}

.outlier-list-head strong {
  color: #334155;
  font-size: 12px;
}

.outlier-list-head span {
  color: #64748b;
  font-size: 12px;
}

.outlier-items {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.outlier-items li {
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 11px;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  background: #fff7ed;
}

.outlier-items div {
  min-width: 0;
}

.outlier-items strong,
.outlier-items span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.outlier-items strong {
  color: #7c2d12;
  font-size: 12px;
}

.outlier-items span {
  margin-top: 3px;
  color: #9a3412;
  font-size: 11px;
}

.outlier-items b {
  color: #c2410c;
  font-size: 14px;
  line-height: 1.2;
}

.list-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
  padding: 0 2px 10px;
}

.list-heading > div {
  display: grid;
  gap: 3px;
}

.list-heading strong {
  color: #0f172a;
  font-size: 15px;
}

.list-heading span {
  color: #64748b;
  font-size: 12px;
}

.pagination {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
}

.pagination button {
  min-width: 36px;
  height: 36px;
  border: 1px solid #dbe4f0;
  border-radius: 9px;
  background: #fff;
  color: #475569;
  font-weight: 700;
  cursor: pointer;
}

.pagination button.active {
  border-color: #2563eb;
  background: #2563eb;
  color: #fff;
}

.pagination button:disabled {
  cursor: default;
  opacity: 0.45;
}

.pagination button.ellipsis {
  border-color: transparent;
  background: transparent;
}

.table-wrap {
  margin-top: 16px;
  border: 1px solid #e5eaf0;
  border-radius: 14px;
  overflow-x: auto;
  overflow-y: hidden;
}

.table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  font-size: 13px;
}

.table thead th {
  background: #f8fafc;
  text-align: left;
  padding: 13px 16px;
  border-bottom: 1px solid #e5eaf0;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
}

.table tbody td {
  padding: 16px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: middle;
  white-space: nowrap;
}

.table tbody tr:last-child td {
  border-bottom: 0;
}

.table tbody tr:hover {
  background: #f8fbff;
}

.device-row {
  cursor: pointer;
  transition: background-color 0.16s ease, box-shadow 0.16s ease;
}

.device-row:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: -2px;
}

.sort-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 800;
  cursor: pointer;
}

.sort-button span {
  min-width: 12px;
  color: #2563eb;
  font-size: 10px;
}

.right {
  text-align: right;
}

.col-name {
  width: 24%;
}

.col-region,
.col-status,
.col-location {
  width: 13%;
}

.status-head {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.status-filter {
  height: 28px;
  min-width: 78px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  padding: 0 8px;
  background: #fff;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.col-usage {
  width: 16%;
}

.col-actions {
  width: 250px;
}

.actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: nowrap;
  align-items: center;
}

.link {
  min-height: 32px;
  border: 1px solid #dbe4f0;
  border-radius: 9px;
  padding: 7px 10px;
  cursor: pointer;
  background: #fff;
  color: #334155;
  font-weight: 700;
  font-size: 12px;
  transition: border-color 0.18s ease, color 0.18s ease, background 0.18s ease;
}

.link:hover {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}

.link-edit {
  border-color: #bbf7d0;
  color: #15803d;
}

.link-edit:hover {
  border-color: #86efac;
  background: #f0fdf4;
  color: #166534;
}

.location {
  display: inline-flex;
  padding: 5px 9px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
}

.status-badge {
  display: inline-flex;
  padding: 5px 9px;
  border: 1px solid #d4d4d4;
  border-radius: 999px;
  background: #f5f5f5;
  color: #262626;
  font-size: 12px;
  font-weight: 800;
}

.status-badge.inactive {
  border-color: #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.status-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.status-elapsed {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.usage-time {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #1e3a8a;
}

.usage-icon {
  display: inline-grid;
  width: 28px;
  height: 28px;
  place-items: center;
  border-radius: 8px;
  background: #eff6ff;
  font-size: 13px;
}

.empty {
  text-align: center;
  color: #64748b;
  padding: 54px 16px !important;
}

/* buttons */
.btn {
  min-height: 40px;
  border-radius: 10px;
  padding: 9px 13px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
}

.btn-primary {
  background: #1d4ed8;
  color: #fff;
}

.btn-ghost {
  background: #fff;
  border-color: #dbe2ea;
  color: #475569;
}

.btn-ghost:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.btn-dark {
  background: #111827;
  color: #fff;
}

.btn-dark:hover:not(:disabled) {
  background: #1f2937;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

/* modal */
.detail-tabs {
  display: flex;
  gap: 4px;
  padding: 10px 22px 0;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.detail-tabs button {
  border: 0;
  border-bottom: 3px solid transparent;
  padding: 11px 14px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.detail-tabs button:hover,
.detail-tabs button.active {
  border-bottom-color: #2563eb;
  color: #1d4ed8;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(5px);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 50;
}

.modal {
  width: 460px;
  max-width: 100%;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: #fff;
  border-radius: 22px;
  padding: 0;
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.3);
}

.modal.wide {
  width: 920px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px;
  border-bottom: 1px solid #edf1f5;
}

.modal-heading {
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
}

.modal-icon {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  background: linear-gradient(145deg, #dbeafe, #eff6ff);
  color: #2563eb;
  font-size: 18px;
}

.modal-icon.month {
  background: linear-gradient(145deg, #ede9fe, #f5f3ff);
  color: #7c3aed;
}

.modal-icon.edit {
  background: linear-gradient(145deg, #dcfce7, #f0fdf4);
  color: #15803d;
}

.modal-title {
  color: #0f172a;
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.modal-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 9px;
}

.modal-location {
  display: inline-flex;
  align-items: center;
  max-width: 240px;
  padding: 3px 8px;
  overflow: hidden;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-subtitle {
  margin: 4px 0 0;
  overflow: hidden;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-close {
  display: grid;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  background: #f1f5f9;
  color: #64748b;
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
  transition: background 0.18s ease, color 0.18s ease;
}

.modal-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.summary-grid {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 12px;
  padding: 20px 24px 0;
}

.summary-card {
  align-content: center;
  position: relative;
  display: grid;
  gap: 4px;
  overflow: hidden;
  padding: 17px 18px;
  border: 1px solid #dedede;
  border-radius: 0;
  background: #fafafa;
}

.summary-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.summary-card strong {
  position: relative;
  z-index: 1;
  margin-top: 2px;
  color: #0f172a;
  font-size: 22px;
  letter-spacing: -0.03em;
}

.summary-note {
  color: #737373;
  font-size: 11px;
}

.query-panel {
  min-width: 0;
  margin: 0;
  padding: 15px 16px;
  border: 1px solid #e5eaf0;
  border-radius: 14px;
  background: #f8fafc;
}

.query-heading {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
}

.query-heading strong {
  color: #334155;
  font-size: 13px;
}

.query-heading span {
  color: #94a3b8;
  font-size: 11px;
}

.date-row {
  display: flex;
  gap: 12px;
  align-items: end;
  flex-wrap: wrap;
}

.field {
  display: grid;
  gap: 6px;
}

.date-row .input {
  height: 40px;
  min-width: 190px;
  border-color: #dbe2ea;
  background: #fff;
}

.button-field {
  padding-bottom: 0;
}

.label {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}

.query-button {
  min-width: 92px;
  box-shadow: 0 5px 12px rgba(37, 99, 235, 0.2);
}

.daily-usage-legend {
  display: flex;
  gap: 16px;
  margin: 0 0 12px;
  color: #64748b;
  font-size: 12px;
}
.daily-usage-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.daily-usage-legend i {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}
.daily-usage-calendar-note {
  color: #64748b;
  font-size: 12px;
}
.chart-section {
  margin: 20px 24px 0;
}

.chart-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 12px;
}

.chart-header > div:first-child {
  display: grid;
  gap: 4px;
}

.chart-header > div:first-child strong {
  color: #1e293b;
  font-size: 14px;
}

.chart-header > div:first-child span {
  color: #94a3b8;
  font-size: 11px;
}

.period-total {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 9px;
  background: #eff6ff;
  color: #64748b;
  font-size: 11px;
  white-space: nowrap;
}

.period-total strong {
  color: #1d4ed8;
  font-size: 12px;
}

.period-total.month {
  background: #f5f3ff;
}

.period-total.month strong {
  color: #6d28d9;
}

.chart-wrap {
  box-sizing: border-box;
  height: 340px;
  border: 1px solid #e5eaf0;
  border-radius: 14px;
  padding: 18px 16px 12px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.chart-empty {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px dashed #dbe2ea;
  border-radius: 14px;
  padding: 32px 12px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  font-size: 13px;
}

.spinner {
  width: 17px;
  height: 17px;
  border: 2px solid #bfdbfe;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

.spinner.month {
  border-color: #ddd6fe;
  border-top-color: #7c3aed;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-actions {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid #edf1f5;
}

.edit-device-modal {
  width: 560px;
}

.edit-device-form {
  display: grid;
  gap: 17px;
  padding: 20px 24px 0;
}

.edit-field {
  display: grid;
  gap: 7px;
}

.edit-field > span,
.edit-time-field legend {
  color: #334155;
  font-size: 12px;
  font-weight: 800;
}

.edit-field .input {
  width: 100%;
}

.edit-field .input[readonly] {
  border-color: #e2e8f0;
  background: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
}

.edit-field .input.invalid {
  border-color: #ef4444;
  background: #fff7f7;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
}

.school-search-field {
  position: relative;
}

.school-selection-error {
  margin: 2px 0 0;
  padding: 10px 12px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.4;
}

.school-search-results {
  display: grid;
  max-height: 230px;
  overflow-y: auto;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.12);
}

.school-search-option {
  display: grid;
  gap: 4px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-bottom: 1px solid #edf1f5;
  background: #fff;
  color: #334155;
  text-align: left;
  cursor: pointer;
}

.school-search-option:last-child {
  border-bottom: 0;
}

.school-search-option:hover,
.school-search-option.active {
  background: #eff6ff;
}

.school-search-option strong {
  font-size: 13px;
  line-height: 1.3;
}

.school-search-option span {
  color: #64748b;
  font-size: 12px;
  line-height: 1.3;
}

.edit-field small,
.edit-time-field small {
  color: #94a3b8;
  font-size: 11px;
}

.edit-time-field {
  display: grid;
  gap: 8px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.edit-time-field legend {
  margin-bottom: 7px;
  padding: 0;
}

.edit-time-inputs {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;
}

.edit-time-inputs label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.edit-time-inputs .input {
  width: 100%;
  min-width: 0;
}

.edit-modal-actions {
  margin: 4px -24px 0;
}

/* 기기별 사용시간 PDF */
.pdf-modal-overlay {
  z-index: 70;
}

.pdf-modal-card {
  display: flex;
  width: min(1280px, 100%);
  height: min(92vh, 920px);
  max-height: calc(100vh - 32px);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.3);
}

.pdf-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.pdf-modal-subtitle {
  margin: 5px 0 0;
  color: #64748b;
  font-size: 12px;
}

.pdf-range-panel {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 14px 22px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
}

.pdf-range-panel .input {
  width: 180px;
  min-width: 0;
  height: 40px;
  background: #fff;
}

.pdf-range-panel .pdf-filter-field .input {
  width: 300px;
}

.range-separator {
  align-self: center;
  margin-top: 18px;
  color: #64748b;
  font-weight: 800;
}

.pdf-result-summary {
  align-self: center;
  margin-left: auto;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.pdf-modal-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #e9eef5;
}

.pdf-frame {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

.pdf-preview-empty {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  box-sizing: border-box;
  color: #64748b;
  text-align: center;
  font-size: 13px;
}

.pdf-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 22px 18px;
  border-top: 1px solid #e5e7eb;
}

.pdf-capture-area {
  position: fixed;
  top: 0;
  left: -100000px;
  z-index: -1;
  width: 1587px;
  background: #fff;
}

.pdf-capture-stack {
  width: 1587px;
  background: #fff;
}

.pdf-page {
  display: flex;
  width: 1587px;
  height: 1123px;
  flex-direction: column;
  padding: 54px 58px 38px;
  box-sizing: border-box;
  overflow: hidden;
  background: #fff;
  color: #111827;
}

.pdf-page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 30px;
  margin-bottom: 24px;
  padding-bottom: 18px;
  border-bottom: 4px solid #1e3a8a;
}

.pdf-page-header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 38px;
  line-height: 1.1;
  letter-spacing: -0.04em;
}

.pdf-page-header p {
  margin: 9px 0 0;
  color: #475569;
  font-size: 19px;
  font-weight: 600;
}

.pdf-page-header > strong {
  color: #475569;
  font-size: 18px;
}

.pdf-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.pdf-table th,
.pdf-table td {
  height: 42px;
  padding: 7px 12px;
  border: 1px solid #94a3b8;
  box-sizing: border-box;
  color: #1e293b;
  font-size: 17px;
  line-height: 1.25;
  text-align: center;
  overflow-wrap: anywhere;
}

.pdf-table th {
  height: 48px;
  background: #e8eef8;
  color: #0f172a;
  font-weight: 800;
}

.pdf-table th:nth-child(2) {
  width: 22%;
}

.pdf-table th:nth-child(3),
.pdf-table th:nth-child(4) {
  width: 12%;
}

.pdf-table th:nth-child(5) {
  width: 16%;
}

.pdf-col-number {
  width: 5%;
}

.pdf-col-usage {
  width: 16%;
}

.pdf-col-period-usage {
  width: 17%;
}

.pdf-table tbody tr:nth-child(even) td {
  background: #f8fafc;
}

.pdf-usage-cell {
  color: #1d4ed8 !important;
  font-weight: 800;
}

.pdf-page-footer {
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 16px;
  color: #64748b;
  font-size: 15px;
}

/* toast */
.toast {
  position: fixed;
  right: 18px;
  bottom: 18px;
  background: rgba(17, 17, 17, 0.92);
  color: #fff;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 13px;
  z-index: 60;
}

@media (max-width: 980px) {
  .page {
    padding: 20px 6px 32px;
  }

  .head {
    align-items: flex-start;
  }

  .h2 {
    font-size: 24px;
  }

  .panel {
    padding: 14px;
  }

  .toolbar {
    align-items: stretch;
  }

  .tool-left {
    min-width: 0;
  }

  .tool-left .input {
    width: 100%;
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .usage-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .outlier-items {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .modal {
    width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    border-radius: 12px;
  }

  .modal.wide {
    width: 96vw;
  }

  .modal-header {
    padding: 18px;
  }

  .summary-grid {
    padding: 16px 18px 0;
  }

  .chart-section {
    margin: 18px 18px 0;
  }

  .modal-actions {
    padding: 14px 18px 18px;
  }

  .input {
    min-width: 180px;
  }

  .chart-wrap {
    height: 220px;
    padding: 10px;
  }

  .date-row {
    flex-direction: column;
    align-items: stretch;
  }

  .pdf-modal-card {
    width: 96vw;
    height: calc(100vh - 20px);
    max-height: none;
    border-radius: 14px;
  }

  .pdf-range-panel {
    flex-wrap: wrap;
    padding: 12px 18px;
  }

  .pdf-result-summary {
    width: 100%;
    margin-left: 0;
  }

  .field {
    width: 100%;
  }

  .button-field {
    width: 100%;
  }

  .button-field .btn {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .head {
    flex-direction: column;
  }

  .device-count {
    width: 100%;
    box-sizing: border-box;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-grid {
    width: 100%;
    grid-template-columns: 1fr;
  }

  .detail-tabs {
    overflow-x: auto;
    padding: 8px 10px 0;
  }

  .detail-tabs button {
    flex: 0 0 auto;
    padding: 10px;
  }

  .tool-right,
  .tool-right .btn {
    width: 100%;
  }

  .tool-right {
    flex-wrap: wrap;
  }

  .list-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .list-heading .btn {
    width: 100%;
  }

  .pagination {
    overflow-x: auto;
    justify-content: flex-start;
    padding-bottom: 4px;
  }

  .usage-stats {
    grid-template-columns: 1fr;
  }

  .stat-card {
    min-height: 0;
  }

  .outlier-list-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }

  .outlier-items {
    grid-template-columns: 1fr;
  }

  .input {
    min-width: 0;
  }

  .modal-overlay {
    padding: 8px;
  }

  .modal.wide {
    width: 100%;
  }

  .modal-header {
    padding: 16px;
  }

  .modal-icon {
    flex-basis: 38px;
    width: 38px;
    height: 38px;
  }

  .modal-title {
    font-size: 17px;
  }

  .summary-grid {
    grid-template-columns: 1fr;
    padding: 14px 14px 0;
  }

  .summary-card {
    padding: 14px 15px;
  }

  .query-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
  }

  .chart-section {
    margin: 16px 14px 0;
  }

  .chart-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .period-total {
    align-self: stretch;
    justify-content: space-between;
  }

  .modal-actions {
    padding: 14px;
  }

  .modal-actions .btn {
    width: 100%;
  }

  .pdf-modal-overlay {
    padding: 0;
  }

  .pdf-modal-card {
    width: 100%;
    height: 100vh;
    border: 0;
    border-radius: 0;
  }

  .pdf-modal-header,
  .pdf-modal-footer {
    padding-right: 14px;
    padding-left: 14px;
  }

  .pdf-range-panel {
    align-items: stretch;
    padding: 12px 14px;
  }

  .pdf-range-panel .field {
    width: calc(50% - 14px);
  }

  .pdf-range-panel .input {
    width: 100%;
  }

  .pdf-range-panel .pdf-filter-field {
    width: 100%;
  }

  .pdf-range-panel .pdf-filter-field .input {
    width: 100%;
  }

  .pdf-range-panel .btn {
    width: 100%;
  }

  .range-separator {
    align-self: flex-end;
    margin-bottom: 11px;
  }
}

.summary-grid .date-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 10px;
}
.summary-grid .field {
  min-width: 0;
  width: auto;
}
.summary-grid .date-row .input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.summary-grid .query-heading {
  flex-wrap: wrap;
}
@media (max-width: 820px) {
  .summary-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .summary-grid .summary-card {
    width: min(240px, 100%);
    box-sizing: border-box;
  }
}
@media (max-width: 480px) {
  .summary-grid .date-row {
    grid-template-columns: minmax(0, 1fr);
  }
}

.device-list-card {
  min-width: 0;
  margin-top: 24px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}
.device-list-card .list-heading {
  margin-top: 0;
}
.device-state-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 20px 0;
}
.state-all,
.state-active {
  --state-soft: #eaf3ff;
  --state-color: #2563eb;
  --state-ink: #1d4ed8;
}
.state-24 {
  --state-soft: #fff0f0;
  --state-color: #dc2626;
  --state-ink: #b91c1c;
}
.state-12 {
  --state-soft: #fff8df;
  --state-color: #b45309;
  --state-ink: #92400e;
}
.state-weekend {
  --state-soft: #fff1e6;
  --state-color: #c2410c;
  --state-ink: #9a3412;
}
.state-holiday {
  --state-soft: #f3edff;
  --state-color: #7c3aed;
  --state-ink: #6d28d9;
}
.state-offline {
  --state-soft: #f1f3f5;
  --state-color: #64748b;
  --state-ink: #475569;
}
.device-state-filters button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: var(--state-soft);
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 10px 14px;
  color: var(--state-ink);
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s;
}
.device-state-filters .state-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  box-sizing: border-box;
  border-radius: 999px;
  background: #fff;
  color: var(--state-ink);
  font-size: 12px;
}
.device-state-filters button.selected {
  background: var(--state-color);
  border-color: var(--state-color);
  color: #fff;
}
.device-state-filters button.selected .state-count {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}
.device-state-filters button:focus-visible {
  outline: 2px solid var(--state-color);
  outline-offset: 3px;
}
.device-list-card .table-wrap {
  border-color: #e2e8f0;
  border-radius: 10px;
}
.device-list-card .table thead th {
  background: #eef5ff;
  color: #475569;
}
.device-list-card .table tbody tr {
  background: #fff;
}
.device-list-card .table tbody td {
  border-bottom-color: #edf0f4;
}
.device-list-card .table tbody .device-row:hover {
  background: #f4f8ff;
}
.device-list-card .status-badge {
  align-items: center;
  gap: 6px;
  background: var(--state-soft);
  color: var(--state-ink);
  border: 0;
  border-radius: 6px;
  font-weight: 700;
}
.device-list-card .state-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  font-size: 12px;
}
.calendar-notice,
.classification-notice {
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}
@media (max-width: 640px) {
  .device-list-card {
    padding: 14px;
  }
  .device-state-filters {
    gap: 8px;
  }
  .device-state-filters button {
    padding: 8px 10px;
  }
}
.col-duration {
  min-width: 150px;
}

.page,
.panel,
.head {
  min-width: 0;
}
.head {
  display: block !important;
}
.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.title-row .h2 {
  margin: 0;
}
.unified-search {
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(100%, 410px);
}
.unified-search .input {
  flex: 1;
  min-width: 0;
  width: 100%;
}
@media (max-width: 640px) {
  .title-row {
    align-items: stretch;
  }
  .unified-search {
    width: 100%;
  }
}
.title-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  width: min(100%, 410px);
  min-width: 0;
}
.title-actions .unified-search {
  flex: 1;
  min-width: 0;
}
@media (max-width: 640px) {
  .title-actions {
    width: 100%;
  }
}
</style>
